import { z } from "zod/v4";

import { ProductVariantEventStatusList } from "../schemas/products";
import { castToString } from "../utils";

import { OfferProductInclusionTypeList } from "./offer-products";
import { S3FileLocationDtoSchema } from "./s3-file-location";
import { AvailableSubscriptionDiscountTypeList } from "./subscriptions";
import { VariationDtoSchema } from "./variations";

export const ProductVariantProductTypeList = [
  "analytics",
  "price",
  "offer",
] as const;

export type ProductVariantProductType =
  (typeof ProductVariantProductTypeList)[number];

export const ProductVariantDtoSchema = z.object({
  id: z.uuid().optional(),
  productId: z.string(),
  variantId: z.string(),
  productTitle: z.string(),
  variantTitle: z.string(),
  experienceId: z.uuid(),
  variationId: z.uuid(),
  offerId: z.uuid().nullish(),
  price: castToString.nullish(),
  compareAtPrice: castToString.nullish(),
  subscriptionDiscount: z.string().nullish(),
  subscriptionDiscountType: z
    .enum(AvailableSubscriptionDiscountTypeList)
    .nullish(),
  handle: z.string().nullish(),
  dupProductId: z.string().nullish(),
  dupVariantId: z.string().nullish(),
  dupHandle: z.string().nullish(),
  cogs: z.string().nullish(),
  isDeleted: z.boolean().optional(),
  eventStatus: z.enum(ProductVariantEventStatusList).nullish(),
  inclusionType: z.enum(OfferProductInclusionTypeList),
  type: z.enum(ProductVariantProductTypeList),
});

export type ProductVariantDto = z.infer<typeof ProductVariantDtoSchema>;

export const ProductVariantWithDeletedDtoSchema =
  ProductVariantDtoSchema.extend({
    variation: VariationDtoSchema.nullish(),
  });

export type ProductVariantWithDeletedDto = z.infer<
  typeof ProductVariantWithDeletedDtoSchema
>;

export const RedactedExperienceProductVariantSchema = z.object({
  id: z.uuid(), //
  productId: z.string(),
  variantId: z.string(),
  productTitle: z.string(),
  price: castToString.nullish(),
  compareAtPrice: castToString.nullish(),
  subscriptionDiscount: z.string().nullish(),
  subscriptionDiscountType: z.string().nullish(),
  handle: z.string().nullish(),
  dupProductId: z.string().nullish(), //
  dupVariantId: z.string().nullish(), //
  variation: z.object({
    id: z.uuid(),
    name: z.string(),
    isControl: z.boolean(),
  }),
});

export type RedactedExperienceProductVariantDto = z.infer<
  typeof RedactedExperienceProductVariantSchema
>;

export const AnalyticsProductVariantActionDtoSchema = z.object({
  type: z.literal("analytics"),
  location: S3FileLocationDtoSchema,
});

export type AnalyticsProductVariantActionDto = z.infer<
  typeof AnalyticsProductVariantActionDtoSchema
>;

export const PriceProductVariantActionDtoSchema = z.object({
  type: z.literal("price"),
  location: S3FileLocationDtoSchema,
});

export type PriceProductVariantActionDto = z.infer<
  typeof PriceProductVariantActionDtoSchema
>;

export const OfferProductVariantActionDtoSchema = z.object({
  type: z.literal("offer"),
  location: S3FileLocationDtoSchema,
});

export type OfferProductVariantActionDto = z.infer<
  typeof OfferProductVariantActionDtoSchema
>;

export const ProductVariantActionDtoSchema = z.union([
  AnalyticsProductVariantActionDtoSchema,
  PriceProductVariantActionDtoSchema,
  OfferProductVariantActionDtoSchema,
]);

export type ProductVariantActionDto = z.infer<
  typeof ProductVariantActionDtoSchema
>;
